import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { AUDIO_ASSET_KEYS } from '../assets/asset-keys.js';
import { BaseScene } from './base-scene.js';
import { playBackgroundMusic } from '../utils/audio-utils.js';
import { addFullscreenBackground } from '../utils/fullscreen-bg.js';
import { netManager, hasWallet, checkAccess } from '../net/index.js';
import { applyAccountToDataManager } from '../world/profile.js';
import { DATA_MANAGER_STORE_KEYS, dataManager } from '../utils/data-manager.js';
import { getStarterOptions, buildStarterMonster } from '../world/starters.js';

/**
 * MMO entry router. Replaces the inherited NEW GAME / CONTINUE title menu. There is ONE shared
 * world, so there is nothing to "start" — the only question is which wallet is playing and
 * whether it already has an account.
 *
 * Flow:
 *   resolve wallet (?wallet= hand-off → trusted injected wallet → Connect Wallet button)
 *     → GET /player?wallet=
 *         ├─ 404 / offline → PROFILE SETUP (new account)
 *         └─ existing      → load profile into data-manager → shared WORLD
 * Wallet is MANDATORY: with no wallet we show a clean "Connect Wallet to play" screen (no demo).
 */
export class EntryScene extends BaseScene {
  /** @type {Phaser.GameObjects.Text} */
  #statusText;
  /** @type {Phaser.GameObjects.Container | undefined} */
  #connectButton;
  /** @type {boolean} */
  #busy;
  /** @type {Phaser.GameObjects.GameObject[]} */
  #responsive;

  constructor() {
    super({ key: SCENE_KEYS.ENTRY_SCENE });
  }

  /** @returns {void} */
  init() {
    super.init();
    this.#busy = false;
    this.#responsive = [];
  }

  /** @returns {void} */
  create() {
    super.create();

    addFullscreenBackground(this);
    this.#buildBranding();
    this.#buildStatus();
    this.#buildConnectButton();

    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, this.#layout, this));

    playBackgroundMusic(this, AUDIO_ASSET_KEYS.TITLE);

    // Begin resolving the session wallet immediately. If the landing page handed off a wallet
    // (or the wallet is already trusted) the player never sees the connect button.
    void this.#begin(false);
  }

  /** @returns {void} */
  #buildBranding() {
    const cx = this.scale.width / 2;
    const titleY = this.scale.height * 0.34;

    const glow = this.add
      .text(cx, titleY + 4, 'MONSTARA', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '110px',
        color: '#0a0a12',
      })
      .setOrigin(0.5)
      .setAlpha(0.5);
    const title = this.add
      .text(cx, titleY, 'MONSTARA', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '110px',
        color: '#ffffff',
        stroke: '#6a3df0',
        strokeThickness: 8,
        shadow: { offsetX: 0, offsetY: 0, color: '#7fd1ff', blur: 22, fill: true },
      })
      .setOrigin(0.5);
    const tagline = this.add
      .text(cx, titleY + 76, 'MONSTER HUNT THE WORLD', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '24px',
        color: '#bda9ff',
      })
      .setOrigin(0.5)
      .setAlpha(0.9);

    this.#responsive.push(glow, title, tagline);
  }

  /** @returns {void} */
  #buildStatus() {
    this.#statusText = this.add
      .text(this.scale.width / 2, this.scale.height * 0.6, 'Connecting…', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '22px',
        color: '#e7e3ff',
        align: 'center',
      })
      .setOrigin(0.5);
    this.#responsive.push(this.#statusText);
  }

  /** A premium pill button (rounded rect + label) for the Connect Wallet action. @returns {void} */
  #buildConnectButton() {
    const w = 320;
    const h = 60;
    const g = this.add.graphics();
    g.fillStyle(0x6a3df0, 1).fillRoundedRect(-w / 2, -h / 2, w, h, 14);
    g.lineStyle(2, 0x9a7bff, 1).strokeRoundedRect(-w / 2, -h / 2, w, h, 14);
    const label = this.add
      .text(0, 0, 'Connect Wallet', {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '26px',
        color: '#ffffff',
      })
      .setOrigin(0.5);

    const container = this.add.container(this.scale.width / 2, this.scale.height * 0.7, [g, label]);
    container.setSize(w, h).setInteractive({ useHandCursor: true });
    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.04));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_DOWN, () => void this.#begin(true));
    container.setVisible(false);
    this.#connectButton = container;
    this.#responsive.push(container);
  }

  /** Re-center responsive elements on viewport resize. @returns {void} */
  #layout() {
    const cx = this.scale.width / 2;
    const titleY = this.scale.height * 0.34;
    const ys = [titleY + 4, titleY, titleY + 76, this.scale.height * 0.6, this.scale.height * 0.7];
    this.#responsive.forEach((obj, i) => {
      const positionable = /** @type {Phaser.GameObjects.Image} */ (/** @type {unknown} */ (obj));
      positionable.setPosition?.(cx, ys[i]);
    });
  }

  /**
   * Resolve the session wallet then branch on the account lookup. `interactive` opens the
   * Phantom popup (Connect Wallet click); the silent first pass uses the URL hand-off / trusted
   * reconnect only.
   * @param {boolean} interactive
   * @returns {Promise<void>}
   */
  async #begin(interactive) {
    if (this.#busy) return;
    this.#busy = true;

    // GUEST / SPECTATOR mode (?spectate=1): skip wallet resolution, the landing redirect, and the
    // connect prompt entirely. Seed a local guest profile and drop straight into the shared world.
    if (this.#isSpectate()) {
      this.#statusText.setText('Entering as spectator…').setColor('#7fffa0');
      this.#enterAsGuest();
      return;
    }

    this.#statusText.setText(interactive ? 'Opening wallet…' : 'Connecting…').setColor('#e7e3ff');
    this.#connectButton?.setVisible(false);

    const wallet = await netManager.resolveWallet({ interactive });
    if (!wallet) {
      this.#busy = false;
      // Not connected + no ?wallet= handoff → send the player through the landing (the "first page")
      // to connect there, then it hands off back to the game with ?wallet=. Cleaner / more pro.
      // Only on the branded prod game domain (play.monstara.fun); fly.dev + localhost keep the
      // in-game connect prompt for testing. Anti-loop: only when there's no ?wallet= in the URL.
      if (this.#shouldRedirectToLanding()) {
        window.location.href = 'https://monstara.fun/?play=1';
        return;
      }
      this.#showConnectPrompt();
      return;
    }

    const short = `${wallet.slice(0, 4)}…${wallet.slice(-4)}`;
    this.#statusText.setText(`Wallet ${short}\nChecking $MONSTARA balance…`).setColor('#7fffa0');

    // Hold-to-play: a wallet below the $MONSTARA minimum gets a limited DEMO preview (roam only — no
    // saving, Gold or cards) instead of the full game, with a prompt to buy in. Demo mode (api
    // unreachable / dev) never blocks. The wallet is already signature-proven at this point.
    const access = await checkAccess(wallet);
    if (access.mode === 'wallet' && !access.allowed) {
      this.#busy = false;
      this.#showHoldGate(access);
      return;
    }

    this.#statusText.setText(`Wallet ${short}\nLoading your account…`).setColor('#7fffa0');

    const lookup = await netManager.lookupAccount(wallet);
    if (lookup.kind === 'existing') {
      // returning player: hydrate the data-manager from the api row, then enter the shared world.
      applyAccountToDataManager(lookup.view);
      this.#enter(SCENE_KEYS.WORLD_SCENE);
      return;
    }

    if (lookup.kind === 'offline') {
      // Couldn't reach the server AND no cached account: NEVER silently re-onboard a returning
      // player — show a retry prompt instead. (lookupAccount already retried + checked the cache.)
      this.#busy = false;
      this.#statusText.setText('Couldn’t reach the server.\nTap Connect to retry.').setColor('#ffd479');
      this.#connectButton?.setVisible(true);
      return;
    }

    // genuinely new wallet (api responded 404) → run fullscreen profile setup.
    this.#enter(SCENE_KEYS.PROFILE_SETUP_SCENE);
  }

  /**
   * True only on the branded prod game domain (play.monstara.fun) when there's no `?wallet=` handoff
   * — so a player who opens the game directly without connecting is sent to the landing first. On
   * fly.dev / localhost we keep the in-game connect prompt for testing.
   * @returns {boolean}
   */
  #shouldRedirectToLanding() {
    if (typeof window === 'undefined') return false;
    // Spectators never get redirected — they enter the world as a guest.
    if (this.#isSpectate()) return false;
    if (window.location.hostname !== 'play.monstara.fun') return false;
    return !new URLSearchParams(window.location.search).get('wallet');
  }

  /** True when the URL carries `?spectate=1` — the GUEST/SPECTATOR entry path. @returns {boolean} */
  #isSpectate() {
    if (typeof window === 'undefined') return false;
    try {
      return new URLSearchParams(window.location.search).get('spectate') === '1';
    } catch {
      return false;
    }
  }

  /**
   * True only when there's NO injected wallet provider AND we're on a mobile browser — where Phantom
   * can only inject inside its own in-app browser, so a plain mobile browser can never connect.
   * @returns {boolean}
   */
  #isMobileNoWallet() {
    if (typeof navigator === 'undefined') return false;
    return !hasWallet() && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  /**
   * Seed a valid LOCAL guest profile (no API account, no wallet) and enter the shared world. Reuses
   * the new-player defaults: startNewGame() resets to the overworld spawn with GAME_STARTED=false (so
   * the world derives the map spawn), then we drop in one starter monster, a "Spectator" name, and a
   * little demo gold. netManager keeps a tokenless GUEST identity (resolveIdentity(null) generates a
   * random guest id on ensureClient), so multiplayer join works as a guest and every API call no-ops
   * offline — nothing here can block on a missing wallet/account. @returns {void}
   */
  #enterAsGuest() {
    // Reset to a clean new-player state. GAME_STARTED stays false so world-scene.init() reads the
    // player spawn from the map data — guaranteeing PLAYER_POSITION/LOCATION load cleanly.
    dataManager.startNewGame();

    // Seed a party so the world (party menu, encounters) has at least one monster to read.
    const options = getStarterOptions();
    const starter = options[0] ? buildStarterMonster(this, options[0].speciesId) : undefined;
    if (starter) {
      dataManager.store.set(DATA_MANAGER_STORE_KEYS.MONSTERS_IN_PARTY, [starter]);
    }

    // Guest identity bits the world/HUD read.
    dataManager.store.set(DATA_MANAGER_STORE_KEYS.PLAYER_NAME, 'Spectator');
    dataManager.setGold(100);
    dataManager.saveData();

    this.#enter(SCENE_KEYS.WORLD_SCENE);
  }

  /** No-wallet state: clean, mandatory connect prompt (no demo / press-space path). @returns {void} */
  #showConnectPrompt() {
    if (hasWallet()) {
      this.#statusText.setText('Connect your Solana wallet to play.').setColor('#e7e3ff');
      this.#connectButton?.setVisible(true);
    } else if (this.#isMobileNoWallet()) {
      // On mobile there's no injected provider (Phantom only injects in its own in-app browser).
      this.#statusText
        .setText("Open this page in your wallet app's browser (e.g. Phantom)\nto connect — or use Spectate to look around.")
        .setColor('#ffd479');
      this.#connectButton?.setVisible(false);
    } else {
      this.#statusText
        .setText('No Solana wallet found.\nInstall Phantom, then reopen from the launch page to play.')
        .setColor('#ffd479');
      this.#connectButton?.setVisible(false);
    }
  }

  /**
   * Hold-to-play gate for a wallet below the $MONSTARA minimum: explain the requirement, offer a Buy
   * link, and let the player into a limited demo preview. @param {import('../net/index.js').AccessResult} access
   * @returns {void}
   */
  #showHoldGate(access) {
    const need = access.required ?? 50_000;
    const have = Math.floor(access.balance ?? 0);
    this.#connectButton?.setVisible(false);
    this.#statusText.setText(''); // the gate panel carries the copy — don't let the shared status line overlap

    const cx = this.scale.width / 2;
    const pw = Math.min(580, this.scale.width - 40);
    const ph = 330;
    const top = this.scale.height * 0.71 - ph / 2; // sit below the MONSTARA title/subtitle
    const FONT = KENNEY_FUTURE_NARROW_FONT_NAME;

    // Glass card so the text is readable over the busy splash art (and the layout can't collide).
    const panel = this.add.graphics().setDepth(10);
    panel.fillStyle(0x0b0a1a, 0.85).fillRoundedRect(cx - pw / 2, top, pw, ph, 22);
    panel.lineStyle(2, 0x9a7bff, 0.9).strokeRoundedRect(cx - pw / 2, top, pw, ph, 22);

    const mk = (dy, text, size, color, wrap) =>
      this.add
        .text(cx, top + dy, text, {
          fontFamily: FONT,
          fontSize: `${size}px`,
          color,
          align: 'center',
          wordWrap: wrap ? { width: pw - 56 } : undefined,
        })
        .setOrigin(0.5)
        .setDepth(11);

    const els = [
      panel,
      mk(42, `🔒  ${need.toLocaleString()} $MONSTARA`, 26, '#ffd479', false),
      mk(82, 'to unlock the full game — saving, Gold & real graded cards', 15, '#e7e3ff', true),
      mk(124, `You hold ${have.toLocaleString()}`, 18, have > 0 ? '#7fffa0' : '#ff9aa2', false),
      mk(156, 'Your account is safe — it unlocks the moment you hold enough.', 13, '#b9b4d0', true),
    ];
    const buy = this.#makeGateButton('Buy $MONSTARA', top + 214, 0x6d28d9, () =>
      this.#openUrl('https://axiom.trade/meme/EqVnBETX7TVHiLZ6muKGixckhmmLHbPTwRgCNhXwnMFe?chain=sol'),
    );
    const preview = this.#makeGateButton('Enter preview (demo)', top + 286, 0x1d3a26, () => {
      [...els, buy, preview].forEach((o) => o.destroy());
      this.#enterAsGuest();
    });
  }

  /** Open an external URL in a new tab reliably (a synthetic <a> click survives popup blockers better
   *  than window.open from a canvas pointer handler). @param {string} url @returns {void} */
  #openUrl(url) {
    try {
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      window.open(url, '_blank');
    }
  }

  /**
   * Build a simple labelled button centered at `y`. @param {string} text @param {number} y
   * @param {number} fill @param {() => void} onClick @returns {Phaser.GameObjects.Container}
   */
  #makeGateButton(text, y, fill, onClick) {
    const w = 300;
    const h = 60;
    const g = this.add.graphics();
    g.fillStyle(fill, 1).fillRoundedRect(-w / 2, -h / 2, w, h, 14);
    g.lineStyle(2, 0x9a7bff, 1).strokeRoundedRect(-w / 2, -h / 2, w, h, 14);
    const label = this.add
      .text(0, 0, text, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '22px', color: '#ffffff' })
      .setOrigin(0.5);
    const c = this.add.container(this.scale.width / 2, y, [g, label]).setDepth(12);
    c.setSize(w, h).setInteractive({ useHandCursor: true });
    c.on(Phaser.Input.Events.POINTER_OVER, () => c.setScale(1.04));
    c.on(Phaser.Input.Events.POINTER_OUT, () => c.setScale(1));
    c.on(Phaser.Input.Events.POINTER_DOWN, () => void onClick());
    return c;
  }

  /**
   * Fade out and start the next scene.
   * @param {string} sceneKey
   * @returns {void}
   */
  #enter(sceneKey) {
    this._controls.lockInput = true;
    this.cameras.main.fadeOut(450, 0, 0, 0);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.scene.start(sceneKey);
    });
  }

  /** @returns {void} */
  update() {
    // BaseScene.update handles the F-key fullscreen toggle. No menu navigation here:
    // the entry flow is wallet-driven (URL hand-off / Connect button), not keyboard menus.
    super.update();
  }
}
